import { SuggestedActions } from "./SuggestedActions";
import type { TextComposerSuggestedAction } from "./suggestedActionsSchema";
import {
  MAX_SUGGESTIONS,
  SUGGESTION_ERROR_FIELDS,
  normalizePhoneNumber,
  suggestedActionSchema,
  suggestedActionsSchema,
  validateSuggestions,
} from "./suggestedActionsSchema";
import type { TextComposerRcsSuggestion } from "../../channel/channelTransforms";
import type { TextComposerExtension } from "../../editor/extensionContract";
import { transformTextComposerMergeTags } from "../mergeTags/mergeTagDefinitions";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";

declare module "../../editor/authoringState" {
  interface TextComposerAuthoringState {
    suggestions?: TextComposerSuggestedAction[];
  }
}

const SUGGESTIONS_OWNED_KEY = "suggestions";

// Real signatures of the actions this extension contributes to the store.
// Consumers cast the store's opaque action bag to this to call them typed.
export type SuggestedActionsActions = {
  addSuggestion: (action: TextComposerSuggestedAction) => void;
  setSuggestion: (index: number, action: TextComposerSuggestedAction) => void;
  duplicateSuggestion: (index: number) => void;
  moveSuggestion: (from: number, to: number) => void;
  removeSuggestion: (index: number) => void;
  clearSuggestions: () => void;
};

function slugifySuggestionText(text: string): string {
  return text
    .replace(/\{%.*?%\}/g, "")
    .replace(/[{}]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function buildPostbackData(
  action: TextComposerSuggestedAction,
  index: number,
  taken: Set<string>,
): string {
  const slug = slugifySuggestionText(action.text) || String(index + 1);
  const base = `${action.type.toLowerCase()}_${slug}`;

  let candidate = base;
  let suffix = 2;
  while (taken.has(candidate)) {
    candidate = `${base}_${suffix}`;
    suffix += 1;
  }

  taken.add(candidate);
  return candidate;
}

function toWireSuggestions(
  actions: TextComposerSuggestedAction[],
  productModel: ProductGridModelValue,
): TextComposerRcsSuggestion[] {
  const taken = new Set<string>();
  // Explicit fields — the authoring `id` is a client-only key and must not
  // reach the wire.
  return actions.map((action, index) => {
    const base = {
      text: transformTextComposerMergeTags(action.text.trim(), productModel),
      postbackData: buildPostbackData(action, index, taken),
    };

    switch (action.type) {
      case "REPLY":
        return { type: "REPLY", ...base };
      case "OPEN_URL":
        return {
          type: "OPEN_URL",
          ...base,
          url: transformTextComposerMergeTags(action.url.trim(), productModel),
          application: "BROWSER",
        };
      case "DIAL_PHONE":
        return {
          type: "DIAL_PHONE",
          ...base,
          phoneNumber: normalizePhoneNumber(action.phoneNumber.trim()),
        };
    }
  });
}

export const suggestedActionsExtension: TextComposerExtension<
  TextComposerSuggestedAction[]
> = {
  id: "suggestedActions",

  stateSchema: suggestedActionsSchema,

  isEmpty: (slice) => slice.length === 0,

  persistence: {
    ownedKeys: [SUGGESTIONS_OWNED_KEY],
    // Never persist blank rows: `validate` runs even when the flag is off (the
    // section is hidden but validation still blocks), so a saved empty row would
    // jam the draft with no way to clear it. They carry no authored content.
    serialize: (slice) => ({
      [SUGGESTIONS_OWNED_KEY]: slice.filter(
        (action) => action.text.trim().length > 0,
      ),
    }),
    deserialize: (raw) => {
      const candidate = raw[SUGGESTIONS_OWNED_KEY];
      if (!Array.isArray(candidate)) return undefined;
      return candidate.flatMap((row) => {
        const parsed = suggestedActionSchema.safeParse(row);
        return parsed.success ? [parsed.data] : [];
      });
    },
  },

  createActions: (set): SuggestedActionsActions => ({
    addSuggestion(action: TextComposerSuggestedAction) {
      set((state) => {
        state.authoringState.suggestions ??= [];
        if (state.authoringState.suggestions.length >= MAX_SUGGESTIONS) return;
        state.authoringState.suggestions.push(action);
      });
    },

    setSuggestion(index: number, action: TextComposerSuggestedAction) {
      set((state) => {
        const suggestions = state.authoringState.suggestions;
        if (!suggestions?.[index]) return;
        suggestions[index] = action;
      });
    },

    duplicateSuggestion(index: number) {
      set((state) => {
        const suggestions = state.authoringState.suggestions;
        const source = suggestions?.[index];
        if (!suggestions || !source) return;
        if (suggestions.length >= MAX_SUGGESTIONS) return;
        suggestions.splice(index + 1, 0, {
          ...source,
          id: crypto.randomUUID(),
        });
      });
    },

    moveSuggestion(from: number, to: number) {
      set((state) => {
        const suggestions = state.authoringState.suggestions;
        if (!suggestions?.[from] || to < 0 || to >= suggestions.length) return;
        const [moved] = suggestions.splice(from, 1);
        suggestions.splice(to, 0, moved);
      });
    },

    removeSuggestion(index: number) {
      set((state) => {
        const suggestions = state.authoringState.suggestions;
        if (!suggestions) return;
        suggestions.splice(index, 1);
        if (suggestions.length === 0) {
          delete state.authoringState.suggestions;
        }
      });
    },

    clearSuggestions() {
      set((state) => {
        delete state.authoringState.suggestions;
      });
    },
  }),

  transformRcs: (state, payload, context) => {
    const suggestions = toWireSuggestions(
      (state.suggestions ?? []).filter(
        (action) => action.text.trim().length > 0,
      ),
      context.productModel as ProductGridModelValue,
    );

    if (suggestions.length === 0) return payload;

    return {
      content: {
        ...payload.content,
        suggestions: [...(payload.content.suggestions ?? []), ...suggestions],
      },
    };
  },

  validate: (state) => {
    const suggestions = state.suggestions ?? [];
    if (suggestions.length === 0) return { valid: true };

    const rowErrors = validateSuggestions(suggestions);
    if (Object.keys(rowErrors).length === 0) return { valid: true };

    const errors: Record<string, string> = {};
    for (const [index, fields] of Object.entries(rowErrors)) {
      for (const field of SUGGESTION_ERROR_FIELDS) {
        const message = fields[field];
        if (message) errors[`${index}.${field}`] = message;
      }
    }
    return { valid: false, errors };
  },

  component: SuggestedActions,
};
